'use client';

import { lazy, Suspense } from 'react';

/**
 * Lazy-loaded file icons to reduce initial bundle size
 * Icons are only loaded when needed
 */
const FileIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.FileIcon })));
const FileTextIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.FileTextIcon })));
const FileSpreadsheetIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.FileSpreadsheetIcon })));
const PresentationIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.PresentationIcon })));
const FileCodeIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.FileCodeIcon })));
const FileArchiveIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.FileArchiveIcon })));
const FileAudioIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.FileAudioIcon })));
const FileVideoIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.FileVideoIcon })));
const FileImageIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.FileImageIcon })));
const FileJsonIcon = lazy(() => import('lucide-react').then(mod => ({ default: mod.FileJsonIcon })));

const IconFallback = () => <div className="h-12 w-12 bg-gray-300 rounded" />;

interface FileIconProps {
  fileCategory: string;
  className?: string;
}

/**
 * Renders the appropriate file icon based on file category
 * Icons are lazy-loaded to reduce initial bundle size
 */
export function FileIconRenderer({ fileCategory, className = 'h-12 w-12' }: FileIconProps) {
  const iconClass = `${className} text-gray-500`;

  switch (fileCategory) {
    case 'pdf':
      return (
        <Suspense fallback={<IconFallback />}>
          <FileTextIcon className={`${iconClass} text-red-500`} />
        </Suspense>
      );
    case 'document':
      return (
        <Suspense fallback={<IconFallback />}>
          <FileTextIcon className={`${iconClass} text-blue-500`} />
        </Suspense>
      );
    case 'spreadsheet':
      return (
        <Suspense fallback={<IconFallback />}>
          <FileSpreadsheetIcon className={`${iconClass} text-green-500`} />
        </Suspense>
      );
    case 'presentation':
      return (
        <Suspense fallback={<IconFallback />}>
          <PresentationIcon className={`${iconClass} text-orange-500`} />
        </Suspense>
      );
    case 'code':
      return (
        <Suspense fallback={<IconFallback />}>
          <FileCodeIcon className={`${iconClass} text-purple-500`} />
        </Suspense>
      );
    case 'archive':
      return (
        <Suspense fallback={<IconFallback />}>
          <FileArchiveIcon className={`${iconClass} text-yellow-600`} />
        </Suspense>
      );
    case 'audio':
      return (
        <Suspense fallback={<IconFallback />}>
          <FileAudioIcon className={`${iconClass} text-pink-500`} />
        </Suspense>
      );
    case 'video':
      return (
        <Suspense fallback={<IconFallback />}>
          <FileVideoIcon className={`${iconClass} text-purple-600`} />
        </Suspense>
      );
    case 'image':
      return (
        <Suspense fallback={<IconFallback />}>
          <FileImageIcon className={`${iconClass} text-blue-400`} />
        </Suspense>
      );
    case 'json':
      return (
        <Suspense fallback={<IconFallback />}>
          <FileJsonIcon className={`${iconClass} text-amber-500`} />
        </Suspense>
      );
    default:
      return (
        <Suspense fallback={<IconFallback />}>
          <FileIcon className={iconClass} />
        </Suspense>
      );
  }
}

